import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import debounce from 'lodash/debounce';
import { useRoute, useRouter } from '@/composables/common/router/useRouter';

/**
 * useQueryList — Factory que elimina el boilerplate de listados paginados
 * con filtros sincronizados con la URL.
 *
 * Elimina la repetición de: filters ref, meta computed, updateUrlQuery,
 * debouncedFetch, watch filters, hydratación desde URL, y cleanup.
 *
 * @param {Object} options
 * @param {Function} options.fetcher - async (params) => data
 *     Recibe los params ya armados, debe devolver { data: [], meta: {} }
 * @param {string[]} [options.extraFilters=[]] - Filtros extra (ej: ['talla', 'categoria'])
 * @param {Object} [options.defaultFilterValues={}] - Valores por defecto para filtros extra
 * @param {number} [options.debounceMs=500] - Delay del debounce
 * @param {boolean} [options.urlSync=true] - Sincronizar filters con URL query
 * @param {boolean} [options.autoFetch=true] - Fetch automático al montar
 * @param {boolean} [options.watchListaId=false] - Hacer fetch si cambia route.params.lista_id
 * @param {'route'|'store'|'none'} [options.listaIdSource='route'] - De dónde sacar lista_id
 * @param {Function} [options.getListaId] - Función que devuelve el listaId.
 *     Requerido si listaIdSource === 'store'. Ej: () => store.getters.getListaId
 */
export function useQueryList({
    fetcher,
    extraFilters = [],
    defaultFilterValues = {},
    debounceMs = 500,
    urlSync = true,
    autoFetch = true,
    watchListaId = false,
    listaIdSource = 'route',
    getListaId = null,
} = {}) {
    if (!fetcher) {
        throw new Error('[useQueryList] fetcher function is required');
    }

    const route = listaIdSource === 'route' ? useRoute() : null;
    const router = urlSync ? useRouter() : null;

    // ── State ───────────────────────────────────────────────────────────────

    const data = ref({ data: [], meta: {} });
    const loading = ref(false);

    const defaultFilters = {
        search: '',
        page: 1,
        per_page: 10,
        ...Object.fromEntries(extraFilters.map(f => [f, defaultFilterValues[f] ?? ''])),
    };

    const filters = ref({ ...defaultFilters });

    const meta = computed(() => data.value.meta || {});

    const listaId = computed(() => {
        if (listaIdSource === 'route' && route) return route.params.lista_id;
        if (listaIdSource === 'store') {
            if (!getListaId) {
                throw new Error(
                    '[useQueryList] listaIdSource "store" requiere la opción getListaId'
                );
            }
            return getListaId();
        }
        return null;
    });

    // ── Fetch ───────────────────────────────────────────────────────────────

    async function fetch(lista_id) {
        const f = filters.value;
        const params = {
            filters: {
                search: f.search,
                page: f.page,
                per_page: f.per_page,
                ...extraFilters.reduce((acc, key) => {
                    if (f[key] !== undefined && f[key] !== '') acc[key] = f[key];
                    return acc;
                }, {}),
            },
            lista_id: lista_id ?? listaId.value,
        };

        loading.value = true;
        try {
            const result = await fetcher(params);
            data.value = result || { data: [], meta: {} };
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useQueryList] Fetch error:', error);
            }
            data.value = { data: [], meta: {} };
        } finally {
            loading.value = false;
        }
    }

    // ── URL Sync ────────────────────────────────────────────────────────────

    const isHydrating = ref(false);

    function updateUrlQuery() {
        if (!urlSync || !router) return;
        const f = filters.value;
        const query = {};

        if (f.search) query.search = f.search;
        if (f.page && f.page > 1) query.page = String(f.page);
        if (f.per_page && f.per_page !== 10) query.per_page = String(f.per_page);
        extraFilters.forEach(key => {
            if (f[key]) query[key] = f[key];
        });

        router.replace({ query }).catch(() => {});
    }

    const debouncedFetch = debounce(id => fetch(id), debounceMs);
    const updateUrlDebounced = urlSync
        ? debounce(() => updateUrlQuery(), debounceMs)
        : null;

    // ── URL sync strategy ───────────────────────────────────────────────────
    // We deliberately DO NOT sync `?search=` to the URL on every keystroke.
    // router.replace on each character triggers a route change that can cause
    // the search input to lose focus mid-typing (the parent view re-renders,
    // the SearchComponent subtree gets repainted and `document.activeElement`
    // falls back to <body>).
    //
    // The watcher below still fetches immediately, but only schedules a URL
    // sync when something OTHER than `search` changed (page, per_page, or any
    // extraFilters). The latest `search` is flushed synchronously on unmount
    // (see onUnmounted) so navigating away and back still preserves the query.

    let prevFilterSnapshot = { ...defaultFilters };

    const nonSearchKeys = () => ['page', 'per_page', ...extraFilters];

    // ── Watchers ────────────────────────────────────────────────────────────

    if (urlSync) {
        watch(filters, newVal => {
            if (isHydrating.value) return;

            // Si cambia el término de búsqueda, reseteamos la página a 1.
            // Si estamos en page>1 con un search que no estaba, el backend
            // devuelve vacío (el set filtrado no tiene tantas páginas).
            const searchChanged = newVal.search !== prevFilterSnapshot.search;
            const pageResetNeeded = searchChanged && newVal.page !== 1;
            if (pageResetNeeded) {
                filters.value.page = 1;
            }

            debouncedFetch();

            // effectiveNew refleja el estado actual de filters (post-reset)
            // para que la comparación de otherChanged y el snapshot sean correctos.
            const effectiveNew = { ...newVal, page: filters.value.page };
            const otherChanged = nonSearchKeys().some(
                key => effectiveNew[key] !== prevFilterSnapshot[key]
            );
            if (otherChanged) {
                updateUrlDebounced();
            }

            prevFilterSnapshot = effectiveNew;
        }, { deep: true });
    }

    if (watchListaId && route) {
        watch(() => route.params.lista_id, (newId, oldId) => {
            if (newId && newId !== oldId) fetch();
        });
    }

    // ── Lifecycle ───────────────────────────────────────────────────────────

    if (autoFetch || urlSync) {
        onMounted(() => {
            if (urlSync && route) {
                const query = route.query;
                const keys = Object.keys(query);
                if (keys.length > 0) {
                    isHydrating.value = true;
                    if (query.search) filters.value.search = query.search;
                    if (query.page) filters.value.page = parseInt(query.page, 10);
                    if (query.per_page) filters.value.per_page = parseInt(query.per_page, 10);
                    extraFilters.forEach(key => {
                        if (query[key]) filters.value[key] = query[key];
                    });
                    nextTick(() => { isHydrating.value = false; });
                }
            }

            if (autoFetch) fetch();
        });
    }

    onUnmounted(() => {
        debouncedFetch.cancel();
        if (updateUrlDebounced) {
            updateUrlDebounced.cancel();
            // Final flush: persist the latest `search` to the URL so navigating
            // away and back (or sharing the URL) keeps the query. This is a
            // single synchronous router.replace right before unmount, so it
            // does not cause the input-focus issue described above.
            updateUrlQuery();
        }
    });

    // ── Reset ───────────────────────────────────────────────────────────────

    function resetFilters() {
        filters.value = { ...defaultFilters };
    }

    // ── Return ──────────────────────────────────────────────────────────────

    return {
        data,
        loading,
        filters,
        meta,
        listaId,
        fetch,
        debouncedFetch,
        updateUrlQuery,
        updateUrlDebounced,
        isHydrating,
        resetFilters,
    };
}
